import { Link } from 'react-router-dom';
import { useRef, useEffect } from 'react';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import PageHeroDecor from '@/components/feature/PageHeroDecor';
import { useScrollAnimation, useStaggerAnimation } from '@/hooks/useScrollAnimation';
import { useSEO } from '@/hooks/useSEO';

const messageBody = [
  {
    heading: '届かない、という現実。',
    paragraphs: [
      '医療・福祉・教育・地域・文化の現場には、深い専門性と誠実な実践があります。しかし、その価値が社会に届いていないことが、あまりにも多い。',
      '「情報はある。実績もある。努力もある。それでも届かない。」この言葉を、私は何度も現場で聞いてきました。患者に伝わらない。求職者に伝わらない。地域に伝わらない。支援者に伝わらない。',
      '問題は、活動の質ではありません。「意味」が言語化されていないこと。「価値」が構造化されていないこと。それが、届かない本当の理由です。',
    ],
  },
  {
    heading: '手段より先に、意味がある。',
    paragraphs: [
      '多くの組織は、「何を発信するか」より先に「どう発信するか」を考えます。SNS、動画、パンフレット——手段の選択が先行し、肝心の「なぜ、この組織が存在するのか」が曖昧なまま発信が続きます。',
      '私たちはその順序を逆にします。まず意味を掘り起こし、言語化し、構造化する。手段はその後です。意味が明確になると、発信の一貫性が生まれ、スタッフが自ら語れるようになり、関わる人が自然と広めてくれる循環が生まれます。',
    ],
  },
  {
    heading: '翻訳、という仕事。',
    paragraphs: [
      '私たちの仕事を一言で表すなら、「翻訳」です。専門性を持ちながら社会に届いていない言葉を、届く言葉に変換する。それが私たちの役割です。',
      '翻訳は、単なる言い換えではありません。組織の歴史・実践・思想を深く理解した上で、受け手の文脈に合わせて再構成する作業です。だから私たちは、まず「聴く」ことから始めます。答えを持ち込まず、対話の中から意味を掘り起こします。',
    ],
  },
  {
    heading: '3年後も誇れる言葉を。',
    paragraphs: [
      '私たちは「納品」で終わる仕事をしません。言葉が組織の文化になり、スタッフが自ら語れるようになり、3年後も誇れる資産として蓄積される——そのような設計を、常に意識しています。',
      '速く、派手に、目立つことを目的とせず、長期的に意味が循環する構造をつくること。それが、私たちがこの仕事を続ける理由です。',
      '意味は、すでにそこにある。私たちは、それを見えるようにするお手伝いをするだけです。',
    ],
  },
];

function FadeIn({ children, delay = 0, className = '' }: { children: React.ReactNode; delay?: number; className?: string }) {
  const { ref, isVisible } = useScrollAnimation();
  return (
    <div
      ref={ref as React.RefObject<HTMLDivElement>}
      className={className}
      style={{
        opacity: isVisible ? 1 : 0,
        transform: isVisible ? 'translateY(0)' : 'translateY(24px)',
        transition: `opacity 0.7s ease ${delay}s, transform 0.7s ease ${delay}s`,
      }}
    >
      {children}
    </div>
  );
}

function MessageBlocks() {
  const { ref, visibleCount } = useStaggerAnimation(messageBody.length);
  return (
    <div ref={ref as React.RefObject<HTMLDivElement>} className="space-y-16">
      {messageBody.map((block, idx) => (
        <div
          key={block.heading}
          style={{
            opacity: visibleCount > idx ? 1 : 0,
            transform: visibleCount > idx ? 'translateY(0)' : 'translateY(20px)',
            transition: 'opacity 0.6s ease, transform 0.6s ease',
          }}
        >
          <h2 className="font-serif-jp font-light text-charcoal tracking-normal-jp leading-snug text-2xl md:text-3xl mb-8">
            {block.heading}
          </h2>
          <div className="space-y-5">
            {block.paragraphs.map((p, i) => (
              <p key={i} className="font-sans-jp text-sm text-muted-brown leading-relaxed-jp">
                {p}
              </p>
            ))}
          </div>
        </div>
      ))}
    </div>
  );
}

function RelatedLinks() {
  const items = [
    { href: '/kangaekata', en: 'Philosophy', label: '考え方', desc: '私たちが大切にしている思想と、仕事の根底にある考え方。' },
    { href: '/dekirukoto', en: 'Services', label: 'できること', desc: '戦略設計・コンテンツ設計・運用支援の3領域での支援内容。' },
    { href: '/cases', en: 'Works', label: '事例', desc: '実際のプロジェクトを通じて、何がどう変わったかの記録。' },
  ];
  const { ref, visibleCount } = useStaggerAnimation(items.length);
  return (
    <div ref={ref as React.RefObject<HTMLDivElement>} className="grid grid-cols-1 md:grid-cols-3 gap-px bg-divider border border-divider">
      {items.map((item, idx) => (
        <Link
          key={item.href}
          to={item.href}
          className="bg-warm-light p-10 md:p-12 flex flex-col gap-5 group hover:bg-warm-white transition-colors duration-300 cursor-pointer"
          style={{
            opacity: visibleCount > idx ? 1 : 0,
            transform: visibleCount > idx ? 'translateY(0)' : 'translateY(20px)',
            transition: 'opacity 0.5s ease, transform 0.5s ease',
          }}
        >
          <div>
            <span className="font-garamond text-xs italic text-muted-light block mb-2 group-hover:text-accent-teal transition-colors duration-200">{item.en}</span>
            <h3 className="font-serif-jp font-light text-charcoal tracking-normal-jp text-xl">{item.label}</h3>
          </div>
          <p className="font-sans-jp text-sm text-muted-brown leading-relaxed-jp flex-1">{item.desc}</p>
          <span className="font-sans-jp text-xs text-muted-light group-hover:text-accent-teal transition-colors duration-200">
            見る →
          </span>
        </Link>
      ))}
    </div>
  );
}

export default function MessagePage() {
  useSEO({
    title: '代表メッセージ | メセナインターコム株式会社',
    description: 'メセナインターコム株式会社代表取締役・宮園篤雄からのメッセージ。「意味は、すでにそこにある。私たちは、それを見えるようにするお手伝いをするだけです。」医療・福祉・教育・地域・文化の価値創造への思い。',
    keywords: '代表メッセージ,宮園篤雄,意味設計,価値創造,医療,福祉,教育,地域,文化,コンサルティング',
    ogTitle: '代表メッセージ | メセナインターコム株式会社',
    ogDescription: '「意味は、すでにそこにある。私たちは、それを見えるようにするお手伝いをするだけです。」代表取締役・宮園篤雄からのメッセージ。',
    ogUrl: 'https://mecena-intercom.co.jp/message',
    canonical: 'https://mecena-intercom.co.jp/message',
  });

  const heroRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const el = heroRef.current;
    if (!el) return;
    requestAnimationFrame(() => {
      el.style.opacity = '1';
      el.style.transform = 'translateY(0)';
    });
  }, []);

  return (
    <div className="bg-warm-white min-h-screen">
      <Navbar />
      <main>
        {/* Hero */}
        <section className="pt-40 pb-16 md:pt-52 md:pb-20 relative overflow-hidden bg-warm-white">
          <PageHeroDecor variant="gold" />
          <div className="max-w-7xl mx-auto px-6 md:px-10 relative z-10">
            <div className="grid grid-cols-1 md:grid-cols-12">
              <div
                ref={heroRef}
                className="md:col-span-8 md:col-start-3"
                style={{ opacity: 0, transform: 'translateY(32px)', transition: 'opacity 0.9s ease 0.1s, transform 0.9s ease 0.1s' }}
              >
                <div className="flex items-center gap-4 mb-10">
                  <span className="block w-6 h-px bg-accent-teal" />
                  <span className="text-[10px] font-sans-jp text-accent-teal tracking-widest-jp uppercase">Message</span>
                </div>
                <h1 className="font-serif-jp font-light text-charcoal tracking-normal-jp leading-tight text-4xl md:text-5xl mb-12">
                  代表メッセージ
                </h1>
              </div>
            </div>
          </div>
        </section>

        {/* Message body */}
        <section className="py-24 md:py-36 bg-warm-white">
          <div className="max-w-7xl mx-auto px-6 md:px-10">
            <div className="grid grid-cols-1 md:grid-cols-12">
              <div className="md:col-span-7 md:col-start-3">
                <MessageBlocks />

                {/* Signature */}
                <FadeIn delay={0.1}>
                  <div className="mt-20 pt-10 border-t border-divider">
                    <p className="font-serif-jp font-light text-charcoal tracking-normal-jp text-xl mb-1">代表取締役　宮園篤雄</p>
                    <p className="font-garamond text-xs italic text-muted-light">Representative Director</p>
                    <p className="font-garamond text-xs italic text-muted-light mt-0.5">mecena intercom inc.</p>
                  </div>
                </FadeIn>
              </div>
            </div>
          </div>
        </section>

        {/* Quote */}
        <section className="texture-message py-20 md:py-28 bg-warm-light relative overflow-hidden">
          {/* 「意味はすでにそこにある」テーマ装飾 — 収束する同心円 */}
          <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[280px] h-[280px] lg:w-[480px] lg:h-[480px] rounded-full border border-accent-gold/10 pointer-events-none" />
          <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[180px] h-[180px] lg:w-[320px] lg:h-[320px] rounded-full border border-accent-amber/13 pointer-events-none" />
          <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[80px] h-[80px] lg:w-[160px] lg:h-[160px] rounded-full border border-accent-gold/18 pointer-events-none" />
          {/* 四隅の小さな菱形 */}
          <div className="absolute top-6 left-6 w-4 h-4 border border-accent-gold/25 rotate-45 pointer-events-none" />
          <div className="absolute top-6 right-6 w-4 h-4 border border-accent-amber/25 rotate-45 pointer-events-none" />
          <div className="absolute bottom-6 left-6 w-4 h-4 border border-accent-gold/22 rotate-45 pointer-events-none" />
          <div className="absolute bottom-6 right-6 w-4 h-4 border border-accent-amber/22 rotate-45 pointer-events-none" />
          <div className="max-w-7xl mx-auto px-6 md:px-10 relative z-10">
            <FadeIn delay={0}>
              <div className="grid grid-cols-1 md:grid-cols-12">
                <div className="md:col-span-8 md:col-start-3 text-center">
                  <span className="font-garamond text-6xl text-divider leading-none block mb-6">&ldquo;</span>
                  <blockquote className="font-garamond text-2xl md:text-3xl italic text-charcoal leading-relaxed mb-8">
                    The meaning is already there.<br />We just help you see it.
                  </blockquote>
                  <p className="font-sans-jp text-sm text-muted-brown leading-relaxed-jp">
                    意味は、すでにそこにある。<br />
                    私たちは、それを見えるようにするお手伝いをするだけです。
                  </p>
                </div>
              </div>
            </FadeIn>
          </div>
        </section>

        {/* Links to related pages */}

        <section className="texture-company py-20 md:py-28 bg-warm-light relative overflow-hidden">
          <div className="max-w-7xl mx-auto px-6 md:px-10">
            <FadeIn delay={0}>
              <div className="flex items-center gap-4 mb-12">
                <span className="block w-6 h-px bg-accent-teal" />
                <span className="text-[10px] font-sans-jp text-accent-teal tracking-widest-jp uppercase">Learn More</span>
              </div>
            </FadeIn>
            <RelatedLinks />
          </div>
        </section>

        {/* CTA */}
        <section className="py-20 md:py-28 bg-warm-white border-t border-divider">
          <FadeIn delay={0}>
            <div className="max-w-7xl mx-auto px-6 md:px-10 flex flex-col md:flex-row items-start md:items-center justify-between gap-8">
              <div>
                <h3 className="font-serif-jp font-light text-charcoal tracking-normal-jp text-2xl mb-3">
                  一緒に、意味をつくりませんか。
                </h3>
                <p className="font-sans-jp text-sm text-muted-brown leading-relaxed-jp">
                  まずは30分、現状の「意味」を一緒に整理しましょう。
                </p>
              </div>
              <div className="flex flex-col sm:flex-row gap-4 flex-shrink-0">
                <Link
                  to="/company"
                  className="inline-flex items-center gap-2 px-7 py-3.5 border border-charcoal text-sm font-sans-jp text-charcoal tracking-wide-jp hover:bg-charcoal hover:text-warm-white transition-all duration-300 whitespace-nowrap"
                >
                  会社情報を見る →
                </Link>
                <Link
                  to="/contact"
                  className="inline-flex items-center gap-2 px-7 py-3.5 bg-charcoal text-warm-white text-sm font-sans-jp tracking-wide-jp hover:bg-charcoal-soft transition-colors duration-300 whitespace-nowrap"
                >
                  相談してみる
                </Link>
              </div>
            </div>
          </FadeIn>
        </section>
      </main>
      <Footer />
    </div>
  );
}
